import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AdminLayout } from './AdminLayout';
import { BlogForm } from './BlogForm';
import { blogAPI } from '../../services/api';
import { toast } from 'sonner';

export const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await blogAPI.getAllPosts();
      setPosts(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (postData) => {
    try {
      await blogAPI.createPost(postData);
      setIsFormOpen(false);
      toast.success('Article créé avec succès');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdatePost = async (postData) => {
    try {
      await blogAPI.updatePost(editingPost.id, postData);
      setEditingPost(null);
      setIsFormOpen(false);
      toast.success('Article modifié avec succès');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeletePost = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        await blogAPI.deletePost(id);
        toast.success('Article supprimé');
        fetchPosts();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      const post = posts.find(p => p.id === id);
      await blogAPI.updatePost(id, { published: !post.published });
      toast.success('Statut de publication mis à jour');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingPost(null);
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion du Blog</h1>
            <p className="text-gray-600">Publiez et gérez vos actualités et newsletters</p>
          </div>
          <Button
            onClick={() => setIsFormOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nouvel article
          </Button>
        </div>

        {/* Search */}
        <Card className="mb-6 bg-white">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Posts List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <Card key={post.id} className="group hover:shadow-lg transition-shadow bg-white overflow-hidden">
              {post.image && (
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  {!post.published && (
                    <div className="absolute top-2 right-2 bg-gray-800/80 px-2 py-1 rounded text-xs text-white">
                      Brouillon
                    </div>
                  )}
                </div>
              )}
              <CardContent className="p-4">
                <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {post.publishedAt 
                      ? new Date(post.publishedAt).toLocaleDateString('fr-FR')
                      : 'Non publié'
                    }
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTogglePublish(post.id)}
                      className={post.published ? 'hover:bg-orange-50' : 'hover:bg-green-50'}
                    >
                      {post.published ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(post)}
                      className="hover:bg-blue-50"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeletePost(post.id)}
                      className="hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun article trouvé</p>
          </div>
        )}
      </div>

      {/* Blog Form Modal */}
      {isFormOpen && (
        <BlogForm
          post={editingPost}
          onSave={editingPost ? handleUpdatePost : handleCreatePost}
          onCancel={handleCloseForm}
        />
      )}
    </AdminLayout>
  );
};